package web.fstech.onthecoffee

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import web.fstech.onthecoffee.auth.*
import web.fstech.onthecoffee.privacy.AppLanguage
import web.fstech.onthecoffee.resources.AppStrings
import web.fstech.onthecoffee.resources.Strings

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuthPortalPage(
    onBack: () -> Unit,
    initialRoute: String = "signin",
    onNavigate: (String) -> Unit = {},
    language: AppLanguage = AppLanguage.ENGLISH,
    onLanguageChange: (AppLanguage) -> Unit = {},
    viewModel: AuthViewModel = remember(initialRoute) { AuthViewModel(initialRoute) }
) {
    val state by viewModel.uiState.collectAsState()
    val strings = AppStrings.get(language)

    LaunchedEffect(viewModel) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is AuthEffect.NavigateToRoute -> onNavigate(effect.route)
                is AuthEffect.NavigateHome -> onBack()
                is AuthEffect.ShowToast -> { /* message displayed in UI status */ }
            }
        }
    }

    LaunchedEffect(initialRoute) {
        val targetTab = AuthTab.fromRoute(initialRoute)
        viewModel.processIntent(AuthIntent.TabChanged(targetTab))
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(strings.authPortalTitle) },
                navigationIcon = {
                    Button(onClick = onBack, modifier = Modifier.padding(8.dp)) {
                        Text(strings.navHome)
                    }
                },
                actions = {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        modifier = Modifier.padding(end = 8.dp)
                    ) {
                        FilterChip(
                            selected = language == AppLanguage.ENGLISH,
                            onClick = { onLanguageChange(AppLanguage.ENGLISH) },
                            label = { Text("English") }
                        )
                        FilterChip(
                            selected = language == AppLanguage.ARABIC,
                            onClick = { onLanguageChange(AppLanguage.ARABIC) },
                            label = { Text("العربية") }
                        )
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                Button(onClick = { onNavigate("signin") }) { Text(strings.navSignIn) }
                Button(onClick = { onNavigate("signup") }) { Text(strings.navSignUp) }
                Button(onClick = { onNavigate("forgot") }) { Text(strings.navForgotPassword) }
                Button(onClick = {
                    if (!SupabaseService.isSignedIn()) {
                        viewModel.processIntent(AuthIntent.TabChanged(AuthTab.DELETE_ACCOUNT))
                    } else {
                        onNavigate("delete")
                    }
                }) { Text(strings.navDeleteAccount) }
            }

            Spacer(modifier = Modifier.height(32.dp))

            when (state.currentTab) {
                AuthTab.SIGN_IN -> SignInScreen(state = state, strings = strings, onIntent = viewModel::processIntent)
                AuthTab.SIGN_UP -> SignUpScreen(state = state, strings = strings, onIntent = viewModel::processIntent)
                AuthTab.FORGOT_PASSWORD -> ForgotPasswordScreen(state = state, strings = strings, onIntent = viewModel::processIntent)
                AuthTab.DELETE_ACCOUNT -> DeleteAccountScreen(state = state, strings = strings, onIntent = viewModel::processIntent)
            }
        }
    }
}

@Composable
fun SignInScreen(
    state: AuthState,
    strings: Strings,
    onIntent: (AuthIntent) -> Unit
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(strings.signInTitle, style = MaterialTheme.typography.headlineMedium)
        OutlinedTextField(
            value = state.email,
            onValueChange = { onIntent(AuthIntent.EmailChanged(it)) },
            label = { Text(strings.labelEmail) }
        )
        OutlinedTextField(
            value = state.password,
            onValueChange = { onIntent(AuthIntent.PasswordChanged(it)) },
            label = { Text(strings.labelPassword) },
            visualTransformation = PasswordVisualTransformation()
        )
        Button(
            onClick = { onIntent(AuthIntent.SubmitSignIn) },
            enabled = !state.isLoading
        ) {
            if (state.isLoading) {
                CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            } else {
                Text(strings.btnSignIn)
            }
        }
        state.statusMessage?.let { Text(it, color = MaterialTheme.colorScheme.primary) }
    }
}

@Composable
fun SignUpScreen(
    state: AuthState,
    strings: Strings,
    onIntent: (AuthIntent) -> Unit
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(strings.signUpTitle, style = MaterialTheme.typography.headlineMedium)
        OutlinedTextField(
            value = state.email,
            onValueChange = { onIntent(AuthIntent.EmailChanged(it)) },
            label = { Text(strings.labelEmail) }
        )
        OutlinedTextField(
            value = state.password,
            onValueChange = { onIntent(AuthIntent.PasswordChanged(it)) },
            label = { Text(strings.labelPassword) },
            visualTransformation = PasswordVisualTransformation()
        )
        Button(
            onClick = { onIntent(AuthIntent.SubmitSignUp) },
            enabled = !state.isLoading
        ) {
            if (state.isLoading) {
                CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            } else {
                Text(strings.btnSignUp)
            }
        }
        state.statusMessage?.let { Text(it) }
    }
}

@Composable
fun ForgotPasswordScreen(
    state: AuthState,
    strings: Strings,
    onIntent: (AuthIntent) -> Unit
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(strings.forgotTitle, style = MaterialTheme.typography.headlineMedium)
        OutlinedTextField(
            value = state.email,
            onValueChange = { onIntent(AuthIntent.EmailChanged(it)) },
            label = { Text(strings.labelEmail) }
        )
        Button(
            onClick = { onIntent(AuthIntent.SubmitForgotPassword) },
            enabled = !state.isLoading
        ) {
            if (state.isLoading) {
                CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            } else {
                Text(strings.btnSendResetLink)
            }
        }
        state.statusMessage?.let { Text(it) }
    }
}

@Composable
fun DeleteAccountScreen(
    state: AuthState,
    strings: Strings,
    onIntent: (AuthIntent) -> Unit
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(strings.deleteTitle, style = MaterialTheme.typography.headlineMedium, color = MaterialTheme.colorScheme.error)
        Text(strings.deleteWarning)

        Row(verticalAlignment = Alignment.CenterVertically) {
            Checkbox(
                checked = state.deleteConfirmed,
                onCheckedChange = { onIntent(AuthIntent.DeleteConfirmedChanged(it)) }
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(strings.deleteConfirmCheck)
        }

        Button(
            enabled = state.deleteConfirmed && !state.isLoading,
            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error),
            onClick = { onIntent(AuthIntent.SubmitDeleteAccount) }
        ) {
            if (state.isLoading) {
                CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            } else {
                Text(strings.btnDeleteMyAccount)
            }
        }
        state.statusMessage?.let { Text(it) }
    }
}
